import type { IntlShape } from 'react-intl';

import { ISODateString } from '../components/iso-date';

import { differenceInDays, getNonFuture } from './time';

const RELATIVE_TIME_UNITS: Array<{
  unit: Intl.RelativeTimeFormatUnit;
  ms: number;
}> = [
  { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
  { unit: 'second', ms: 1000 },
];

/**
 * Converts a timestamp to a human-readable relative time string (e.g. "3 hours ago",
 * "yesterday") by finding the largest time unit that fits the elapsed duration.
 * Uses `numeric: 'auto'` so single-unit values read naturally ("yesterday" vs "1 day ago").
 *
 * The v7 API (`intl.formatRelativeTime`) requires the caller to specify the time unit,
 * so we cascade from largest to smallest to find the best fit.
 */
export function formatRelativeDate(timestamp: number, intl: IntlShape): string {
  const diff = Math.max(0, Date.now() - timestamp);
  for (const { unit, ms } of RELATIVE_TIME_UNITS) {
    const value = Math.floor(diff / ms);
    if (value >= 1) {
      return intl.formatRelativeTime(-value, unit, { numeric: 'auto' });
    }
  }
  // Less than 1 second ago
  return intl.formatRelativeTime(0, 'second', { numeric: 'auto' });
}

/**
 * Formats a date for compact UI metadata.
 * Future dates are clamped to now, and
 * dates older than 7 days fall back to ISO formatting.
 */
export function formatRelativeDateWithIsoFallback(
  date: Date | string | number,
  intl: IntlShape
): string {
  const adjustedDate = getNonFuture(date);

  return differenceInDays(adjustedDate) > 7
    ? ISODateString(adjustedDate)
    : formatRelativeDate(adjustedDate.getTime(), intl);
}
